import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Typography,
  Chip,
  useTheme,
  Collapse,
  Tooltip,
  Divider,
  Switch,
  Stack,
} from "@mui/material";
import {
  Close as CloseIcon,
  DirectionsCar as CarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  MyLocation as MyLocationIcon,
  ZoomOutMap as ZoomOutMapIcon,
  Map as MapIcon,
  ContentCopy as CopyIcon,
  Route as RouteIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Share as ShareIcon,
  Store as StoreIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useAppSelector } from "../../../../app/hooks";
import { HosDriverProfileDto, Vehicle } from "../../../../api/vehiclesApi";
import { UserRoles } from "../../../../constants/roles";
import { selectCurrentUser } from "../../../auth/authSlice";
import { ClosestShopsCard } from "./activetooltips";
import { GooglePlaceWithDistance } from "../../../../types/googlePlaces";
import {
  useCreateShopMutation,
  useUpdateShopMutation,
} from "../../../../api/shopsApi";
import { Shop } from "../../../../types/shop";
import ShopFormModal from "../../../shops/components/ShopFormModal";
import { ShareLinkDialog } from "./ShareLinkDialog";
import { TimeProgressBar } from "../shared/TimeProgressBar";

interface MapTooltipsRowProps {
  selectedVehicle: Vehicle | undefined;
  isSidebarOpen: boolean;
  trailerSidebarOpen?: boolean;
  closestShops: Array<{
    shop: any;
    aerialDistance: number;
    distanceText: string;
    drivingDistance?: string;
    drivingDuration?: string;
  }>;
  isShopsLoading: boolean;
  isCalculatingDriving?: boolean;
  shopsError: string | null;
  onClose?: () => void;
  onGoogleShopSelect?: (shops: any) => void;
  // Shop markers toggle
  showShops?: boolean;
  onShopsToggle?: () => void;
  // Route toggle
  showRoute?: boolean;
  onRouteToggle?: () => void;
  isRouteLoading?: boolean;
  // Map panning
  onPanToShops?: () => void;
  onCenterShop?: (shop: any) => void;
  onPanOut?: () => void;
  // Vehicle actions
  onCenterVehicle?: () => void;
  onPanOutRoute?: () => void;
  /** From HOS profile (employee match); used for HOS preview and driver phone. */
  hosProfile?: HosDriverProfileDto | null;
}

function buildTelHref(phone: string | null | undefined): string | undefined {
  if (!phone?.trim()) return undefined;
  const t = phone.trim();
  const hasPlus = t.startsWith("+");
  const digitsOnly = t.replace(/\D/g, "");
  if (!digitsOnly) return undefined;
  const dialString = hasPlus ? `+${digitsOnly}` : digitsOnly;
  return `tel:${dialString}`;
}

export const MapTooltipsRow: React.FC<MapTooltipsRowProps> = ({
  selectedVehicle,
  isSidebarOpen,
  trailerSidebarOpen = true,
  closestShops,
  isShopsLoading,
  isCalculatingDriving = false,
  shopsError,
  onClose,
  onGoogleShopSelect,
  showShops = false,
  onShopsToggle,
  showRoute = false,
  onRouteToggle,
  isRouteLoading = false,
  onPanToShops,
  onCenterShop,
  onPanOut,
  onCenterVehicle,
  onPanOutRoute,
  hosProfile,
}) => {
  const theme = useTheme();
  const user = useAppSelector(selectCurrentUser);
  const canShareLiveLocation = user?.role !== UserRoles.TRUCK_DRIVER;
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Share live location dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Google shops state - lifted up to persist across collapse/expand
  const [googleShops, setGoogleShops] = useState<GooglePlaceWithDistance[]>([]);
  const [convertedShops, setConvertedShops] = useState<Shop[]>([]);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  // Active tab state - also lifted up to persist across collapse/expand
  const [activeTab, setActiveTab] = useState<"trusted" | "google">("trusted");
  // Shop form modal state
  const [isShopFormModalOpen, setIsShopFormModalOpen] = useState(false);
  const [shopToAdd, setShopToAdd] = useState<Shop | null>(null);
  const [createShop, { isLoading: isCreatingShop }] = useCreateShopMutation();
  const [, { isLoading: isUpdatingShop }] = useUpdateShopMutation();

  // Handle adding shop from Google results
  const handleAddShop = (shop: Shop, preference: number) => {
    // Create a shop object with the preference set, but treat it as a new shop
    // (Google shops have negative IDs, so we set it to null/undefined to indicate new shop)
    const shopWithPreference: Shop = {
      ...shop,
      id: -1, // Temporary ID to indicate it's a new shop from Google
      preference: preference,
      isActive: true, // Default to active
    };
    setShopToAdd(shopWithPreference);
    setIsShopFormModalOpen(true);
  };

  const handleShopFormClose = () => {
    setIsShopFormModalOpen(false);
    setShopToAdd(null);
  };

  const handleShopFormSubmit = async (
    values: Omit<Shop, "id" | "createdAt" | "updatedAt">
  ) => {
    try {
      // Always create new shop (Google shops are always new)
      const result = await createShop(values).unwrap();
      if (result.requestCreated) {
        console.info("DNU request created for shop", result.requestId);
      }
      handleShopFormClose();
    } catch (error) {
      console.error("Failed to save shop:", error);
    }
  };

  // Don't render anything if no vehicle is selected
  if (!selectedVehicle) {
    return null;
  }

  const driverTelHref = buildTelHref(hosProfile?.phoneNumber);
  const vehicleSidebarWidth = 280;
  const trailerSidebarWidth = 260;
  const tooltipLeft =
    (isSidebarOpen ? vehicleSidebarWidth : 0) +
    (trailerSidebarOpen ? trailerSidebarWidth : 0) +
    16;

  const mapActionIconSx = {
    border: 1,
    borderColor: "primary.main",
    color: "primary.main",
    borderRadius: 1,
    width: 30,
    height: 30,
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "primary.light",
      color: "white",
      borderColor: "primary.dark",
    },
  };

  const hasFaults =
    selectedVehicle.lastEmissionsLightOn ||
    selectedVehicle.lastProtectLightOn ||
    selectedVehicle.lastStopLightOn ||
    selectedVehicle.lastWarningLightOn ||
    selectedVehicle.lastCheckEngineLightOn ||
    selectedVehicle.lastFaultSpnDescription ||
    selectedVehicle.lastDtcDescription;

  const faultSeverityColor = selectedVehicle.lastStopLightOn
    ? "error.main"
    : selectedVehicle.lastProtectLightOn || selectedVehicle.lastWarningLightOn
    ? "warning.main"
    : "info.main";

  const faultSummary =
    selectedVehicle.lastFaultSpnDescription ??
    selectedVehicle.lastDtcDescription ??
    (selectedVehicle.lastStopLightOn
      ? "Stop Light"
      : selectedVehicle.lastProtectLightOn
      ? "Protect Light"
      : selectedVehicle.lastWarningLightOn
      ? "Warning Light"
      : selectedVehicle.lastCheckEngineLightOn
      ? "Check Engine Light"
      : selectedVehicle.lastEmissionsLightOn
      ? "Emissions Light"
      : "Active fault");

  return (
    <Card
      sx={{
        position: "absolute",
        top: 16,
        left: tooltipLeft, // sidebar widths + gap
        width: "fit-content",
        maxWidth: `calc(100% - ${tooltipLeft}px - 48px)`,
        zIndex: 1100,
        boxShadow: 3,
        borderRadius: 2,
        bgcolor:
          theme.palette.mode === "light"
            ? "rgba(255, 255, 255, 0.95)"
            : "rgba(33, 33, 33, 0.95)",
        backdropFilter: "blur(8px)",
        border: "1px solid",
        borderColor: theme.palette.divider,
        overflow: "visible",
      }}
    >
      <CardContent
        sx={{
          p: 1,
          paddingBottom: 0,
          position: "relative",
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            alignItems: "stretch",
            minWidth: 0,
            width: "fit-content",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
        {/* Selected vehicle details */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            width: 320,
            flex: "0 0 320px",
            pr: 1,
            borderRight: 1,
            borderColor: "divider",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
              width: "100%",
            }}
          >
            <CarIcon
              color="primary"
              fontSize="small"
              sx={{ flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle2"
                fontWeight="medium"
                sx={{
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={selectedVehicle.name}
              >
                {selectedVehicle.name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.3,
                  mt: 0.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={`${selectedVehicle.make || "Unknown"} ${
                  selectedVehicle.model || ""
                } ${selectedVehicle.year || ""}`.trim()}
              >
                {selectedVehicle.make || "Unknown"} {selectedVehicle.model || ""}{" "}
                {selectedVehicle.year || ""}
              </Typography>
              {selectedVehicle.lastAddress && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.25,
                    minWidth: 0,
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: "0.75rem", color: "text.secondary", flexShrink: 0 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                    title={selectedVehicle.lastAddress}
                  >
                    {selectedVehicle.lastAddress}
                  </Typography>
                </Box>
              )}
            </Box>
            <Chip
              label={selectedVehicle.lastStatus || "Unknown"}
              size="small"
              color={
                selectedVehicle.lastStatus === "Driving"
                  ? "success"
                  : selectedVehicle.lastStatus === "Idle"
                  ? "warning"
                  : "default"
              }
              variant="outlined"
              sx={{ flexShrink: 0 }}
            />
            {selectedVehicle.gatewayHealthStatus === "Unplugged" && (
              <Chip
                label="Gateway Unplugged"
                size="small"
                sx={{
                  flexShrink: 0,
                  bgcolor: "#f4433620",
                  color: "#f44336",
                  border: "1px solid #f4433650",
                  fontWeight: 600,
                }}
              />
            )}
            <IconButton
              type="button"
              size="small"
              aria-label="Close vehicle"
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onClose?.();
              }}
              sx={{
                flexShrink: 0,
                width: 28,
                height: 28,
                ml: "auto",
                color: "text.secondary",
                "&:hover": {
                  bgcolor: "action.hover",
                  color: "text.primary",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              width: "100%",
              minWidth: 0,
              overflowX: "auto",
              overflowY: "hidden",
              pb: 0.25,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  fontSize: "0.6rem",
                  lineHeight: 1,
                  mb: 0.375,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Map
              </Typography>
              <Stack direction="row" spacing={0.25} alignItems="center">
                {onCenterVehicle && (
                  <Tooltip title="Center on vehicle">
                    <IconButton
                      size="small"
                      onClick={onCenterVehicle}
                      aria-label="Center on vehicle"
                      sx={mapActionIconSx}
                    >
                      <MyLocationIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {onRouteToggle && (
                  <Tooltip
                    title={
                      isRouteLoading
                        ? "Loading route…"
                        : "Show or hide vehicle route"
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.25,
                        px: 0.375,
                        py: 0.125,
                        borderRadius: 1,
                        border: 1,
                        borderColor: "primary.main",
                        bgcolor:
                          showRoute && !isRouteLoading
                            ? "primary.light"
                            : "transparent",
                        flexShrink: 0,
                        "&:hover": {
                          bgcolor:
                            showRoute && !isRouteLoading
                              ? "primary.main"
                              : "primary.light",
                        },
                      }}
                    >
                      {isRouteLoading ? (
                        <CircularProgress
                          size={14}
                          sx={{ color: "primary.main", mx: 0.25 }}
                        />
                      ) : (
                        <RouteIcon
                          sx={{
                            fontSize: "1rem",
                            color: showRoute ? "white" : "primary.main",
                          }}
                        />
                      )}
                      <Switch
                        size="small"
                        checked={showRoute}
                        disabled={isRouteLoading}
                        onChange={onRouteToggle}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "white",
                            "& + .MuiSwitch-track": {
                              backgroundColor: "white",
                            },
                          },
                          "& .MuiSwitch-switchBase": {
                            color: "primary.main",
                            "& + .MuiSwitch-track": {
                              backgroundColor: "primary.main",
                            },
                          },
                        }}
                      />
                    </Box>
                  </Tooltip>
                )}

                {onPanOutRoute && showRoute && (
                  <Tooltip title="Pan out to show full route">
                    <IconButton
                      size="small"
                      onClick={onPanOutRoute}
                      aria-label="Pan out to show full route"
                      sx={mapActionIconSx}
                    >
                      <ZoomOutMapIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  fontSize: "0.6rem",
                  lineHeight: 1,
                  mb: 0.375,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Location
              </Typography>
              <Stack direction="row" spacing={0.25} alignItems="center">
                {selectedVehicle?.lastLatitude &&
                  selectedVehicle?.lastLongitude && (
                    <>
                      <Tooltip title="Open location in Google Maps">
                        <IconButton
                          size="small"
                          aria-label="Open location in Google Maps"
                          onClick={() => {
                            const url = `https://www.google.com/maps?q=${selectedVehicle.lastLatitude},${selectedVehicle.lastLongitude}`;
                            window.open(url, "_blank");
                          }}
                          sx={mapActionIconSx}
                        >
                          <MapIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Copy coordinates to clipboard">
                        <IconButton
                          size="small"
                          aria-label="Copy coordinates to clipboard"
                          onClick={async () => {
                            const coordinates = `${selectedVehicle.lastLatitude},${selectedVehicle.lastLongitude}`;
                            try {
                              await navigator.clipboard.writeText(coordinates);
                              console.log(
                                "Coordinates copied to clipboard:",
                                coordinates
                              );
                            } catch (err) {
                              console.error(
                                "Failed to copy coordinates:",
                                err
                              );
                              const textArea =
                                document.createElement("textarea");
                              textArea.value = coordinates;
                              document.body.appendChild(textArea);
                              textArea.select();
                              document.execCommand("copy");
                              document.body.removeChild(textArea);
                            }
                          }}
                          sx={mapActionIconSx}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}

                {canShareLiveLocation && (
                  <Tooltip title="Share live location">
                    <IconButton
                      size="small"
                      aria-label="Share live location"
                      onClick={() => setShareDialogOpen(true)}
                      sx={mapActionIconSx}
                    >
                      <ShareIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  fontSize: "0.6rem",
                  lineHeight: 1,
                  mb: 0.375,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Driver
              </Typography>
              <Tooltip
                title={
                  driverTelHref
                    ? `Call ${selectedVehicle.name || "driver"}`
                    : "Phone number not available"
                }
              >
                <span>
                  <IconButton
                    component={driverTelHref ? "a" : "button"}
                    href={driverTelHref}
                    size="small"
                    aria-label="Call driver"
                    disabled={!driverTelHref}
                    sx={{
                      ...mapActionIconSx,
                      color: driverTelHref ? "primary.main" : "action.disabled",
                      borderColor: driverTelHref
                        ? "primary.main"
                        : "action.disabled",
                    }}
                  >
                    <PhoneIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Stack>

          <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
            <Box>
              {hosProfile && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ px: 0.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        fontSize: "0.6rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        mb: 0.25,
                      }}
                    >
                      HOS
                    </Typography>
                    <TimeProgressBar
                      label="Break"
                      value={hosProfile.breakTime}
                      color="#2196F3"
                      max={480}
                    />
                    <TimeProgressBar
                      label="Drive"
                      value={hosProfile.driveTime}
                      color="#4CAF50"
                      max={660}
                    />
                    <TimeProgressBar
                      label="Shift"
                      value={hosProfile.shiftTime}
                      color="#FF9800"
                      max={840}
                    />
                    <TimeProgressBar
                      label="Cycle"
                      value={hosProfile.cycleTime}
                      color="#9C27B0"
                      max={4200}
                    />
                  </Box>
                </>
              )}

              {hasFaults && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 0.5,
                    }}
                  >
                    <WarningIcon
                      sx={{ fontSize: "0.85rem", color: faultSeverityColor }}
                    />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {faultSummary}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Tooltips Content - Collapsible */}
        <Collapse
          in={!isCollapsed}
          timeout="auto"
          unmountOnExit
          orientation="horizontal"
          sx={{
            flex: "1 1 260px",
            minWidth: 0,
            maxWidth: 300,
            "& .MuiCollapse-wrapperInner": {
              width: "100%",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              minWidth: 0,
              width: 300,
              maxWidth: "100%",
            }}
          >
            {/* Closest Shops Card */}
            <ClosestShopsCard
              selectedVehicle={selectedVehicle}
              closestShops={closestShops}
              isLoading={isShopsLoading}
              isCalculatingDriving={isCalculatingDriving}
              error={shopsError}
              onGoogleShopSelect={onGoogleShopSelect}
              showShops={showShops}
              onShopsToggle={onShopsToggle}
              onPanToShops={onPanToShops}
              onCenterShop={onCenterShop}
              onPanOut={onPanOut}
              onAddShop={handleAddShop}
              // Google shops state
              googleShops={googleShops}
              setGoogleShops={setGoogleShops}
              convertedShops={convertedShops}
              setConvertedShops={setConvertedShops}
              isGoogleLoading={isGoogleLoading}
              setIsGoogleLoading={setIsGoogleLoading}
              googleError={googleError}
              setGoogleError={setGoogleError}
              hasSearched={hasSearched}
              setHasSearched={setHasSearched}
              // Active tab state
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />

            {/* Future tooltips can be added here */}
            {/* 
            <Card sx={{ minWidth: 200, boxShadow: 3, borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle2">Another Tooltip</Typography>
              </CardContent>
            </Card>
            */}
          </Box>
        </Collapse>
        </Box>

        <IconButton
          onClick={() => setIsCollapsed(!isCollapsed)}
          size="small"
          aria-label={isCollapsed ? "Expand nearest shops" : "Collapse nearest shops"}
          sx={{
            minWidth: 28,
            width: 36,
            height: 28,
            borderRadius: 999,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            p: 0,
            position: "absolute",
            right: -16,
            top: "50%",
            transform: "translateY(-50%)",
            boxShadow: 1,
            zIndex: 2000,
            "&:hover": {
              bgcolor: "background.paper",
              borderColor: "primary.main",
            },
            "& .MuiSvgIcon-root": {
              fontSize: "15px",
              color: "text.secondary",
            },
          }}
        >
          {isCollapsed ? (
            <StoreIcon sx={{ mr: -0.25 }} />
          ) : (
            <ChevronLeftIcon sx={{ ml: -0.25 }} />
          )}
          {isCollapsed ? (
            <ChevronRightIcon sx={{ ml: -0.25 }} />
          ) : (
            <StoreIcon sx={{ ml: -0.25 }} />
          )}
        </IconButton>
      </CardContent>

      {canShareLiveLocation && (
        <ShareLinkDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          initialVehicle={selectedVehicle}
        />
      )}

      {/* Shop Form Modal */}
      <ShopFormModal
        open={isShopFormModalOpen}
        onClose={handleShopFormClose}
        onSubmit={handleShopFormSubmit}
        initialValues={shopToAdd}
        isLoading={isCreatingShop || isUpdatingShop}
      />
    </Card>
  );
};
